import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User } from "@supabase/supabase-js";

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const initDone = useRef(false);

  useEffect(() => {
    let isMounted = true;

    const checkAdminRole = async (userId: string) => {
      try {
        // Używamy funkcji backendowej (SECURITY DEFINER), aby sprawdzanie roli
        // działało niezależnie od polityk RLS na tabeli user_roles.
        const { data, error } = await supabase.rpc("has_role", {
          _user_id: userId,
          _role: "admin",
        });

        console.log("[useAuth] checkAdminRole for", userId, "-> data:", data, "error:", error);

        if (error) {
          console.error("[useAuth] has_role RPC error:", error);
          return false;
        }
        return data === true;
      } catch (e) {
        console.error("[useAuth] checkAdminRole exception:", e);
        return false;
      }
    };

    // Set up auth state listener first
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!isMounted) return;
        
        console.log("[useAuth] onAuthStateChange event:", event, "user:", session?.user?.id);
        
        setUser(session?.user ?? null);
        
        if (session?.user) {
          // Małe opóźnienie, żeby sesja się ustabilizowała (token był dostępny dla RPC)
          setTimeout(async () => {
            if (!isMounted) return;
            const adminStatus = await checkAdminRole(session.user.id);
            if (isMounted) {
              setIsAdmin(adminStatus);
              console.log("[useAuth] Admin status set to:", adminStatus);
            }
          }, 100);
        } else {
          setIsAdmin(false);
        }
        
        // Mark loading as done after first auth event
        if (!initDone.current) {
          initDone.current = true;
          setLoading(false);
        }
      }
    );

    // Timeout fallback - if no auth event after 3s, stop loading
    const timeout = setTimeout(() => {
      if (isMounted && !initDone.current) {
        initDone.current = true;
        setLoading(false);
      }
    }, 3000);

    // Get initial session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!isMounted || initDone.current) return;
      
      setUser(session?.user ?? null);
      
      if (session?.user) {
        const adminStatus = await checkAdminRole(session.user.id);
        if (isMounted) {
          setIsAdmin(adminStatus);
        }
      }
      
      initDone.current = true;
      setLoading(false);
    }).catch(() => {
      if (isMounted && !initDone.current) {
        initDone.current = true;
        setLoading(false);
      }
    });

    return () => {
      isMounted = false;
      clearTimeout(timeout);
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
  };

  const signOut = async () => {
    // Wylogowanie lokalne jest bardziej niezawodne w sytuacji, gdy request do backendu
    // nie dojdzie (np. problemy z ciasteczkami / siecią / domeną).
    const { error } = await supabase.auth.signOut({ scope: "local" });
    if (error) throw error;
  };

  return { user, isAdmin, loading, signIn, signOut };
};
